import type { ForqSupabaseClient } from './client';
import type {
  CustomerDraft,
  ImportContext,
  ImportGateway,
  ImportResult,
  MachineDraft,
} from '@forq/import-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

interface ContextRow {
  readonly customers?: readonly { readonly id: string; readonly number: string; readonly name: string }[];
  readonly machineNumbers?: readonly string[];
  readonly machineTypes?: readonly { readonly id: string; readonly name: string }[];
}

interface ResultRow {
  readonly created?: number;
  readonly updated?: number;
}

/**
 * Bewusst in Blöcken statt in einem Rutsch: 1192 Kunden bzw. 2232 Maschinen in EINER Anweisung
 * hiessen ein jsonb von mehreren hundert Kilobyte, ein Statement-Timeout mitten drin -- und danach
 * weiss niemand, was schon drin ist. Jeder Block ist eine eigene Transaktion, die entweder ganz
 * oder gar nicht wirkt, und die Übernahme ist über die Nummer wiederholbar: ein zweiter Anlauf
 * aktualisiert, was schon da ist, statt es zu verdoppeln.
 */
const chunkSize = 200;

function chunked<T>(items: readonly T[], size: number): readonly (readonly T[])[] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

export class SupabaseImportGateway implements ImportGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async loadContext(tenantId: string): Promise<ImportContext> {
    const { data, error } = await this.client.rpc('import_context', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('import.context', messageOf(error));

    const row = (data ?? {}) as ContextRow;
    return {
      customers: (row.customers ?? []).map((entry) => ({
        id: entry.id,
        number: entry.number,
        name: entry.name,
      })),
      machineNumbers: [...(row.machineNumbers ?? [])],
      machineTypes: (row.machineTypes ?? []).map((entry) => ({ id: entry.id, name: entry.name })),
    };
  }

  async importCustomers(tenantId: string, drafts: readonly CustomerDraft[]): Promise<ImportResult> {
    return this.runInChunks('import_customers', tenantId, drafts);
  }

  async importMachines(tenantId: string, drafts: readonly MachineDraft[]): Promise<ImportResult> {
    return this.runInChunks('import_machines', tenantId, drafts);
  }

  private async runInChunks(
    functionName: string,
    tenantId: string,
    drafts: readonly unknown[],
  ): Promise<ImportResult> {
    let created = 0;
    let updated = 0;
    for (const chunk of chunked(drafts, chunkSize)) {
      const { data, error } = await this.client.rpc(functionName, {
        p_tenant_id: tenantId,
        p_rows: chunk,
      });
      if (error) {
        // Die Zahlen der bereits geschriebenen Blöcke gehören in die Meldung: "es sind schon 400
        // drin" ist die Information, die man braucht, um zu entscheiden, was jetzt zu tun ist.
        throw new SupabaseOperationError(
          `import.${functionName}`,
          `${messageOf(error)} (bereits übernommen: ${created} neu, ${updated} aktualisiert)`,
        );
      }
      const row = (data ?? {}) as ResultRow;
      created += Number(row.created ?? 0);
      updated += Number(row.updated ?? 0);
    }
    return { created, updated };
  }
}
